import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, num } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { HeightClearance, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const AIRSPACE_OFFICER_ROLES = ['AIRSPACE_OFFICER'];
const STATES = ['SUBMITTED', 'AUTO_APPROVED', 'REQUIRES_REVIEW', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'WITHDRAWN'];

/**
 * M23 — height clearances for structures/cranes/towers: auto-checked
 * against proposed height + C9 AVIATION_HEIGHT obstacle surfaces at submit
 * time; below the 45m AGL threshold and clear of obstacles auto-issues
 * instantly and free, otherwise AIRSPACE_OFFICER assessment (with fee) is
 * required. Also consumed via referral from other modules (e.g. m17
 * planning) — referralSourceModule/referralCaseRef surface that linkage.
 */
export function HeightClearances() {
  const { persona } = useAuth();
  const isAirspaceOfficer = persona ? persona.roles.some((r) => AIRSPACE_OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m23/height-clearances?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<HeightClearance>>(path);

  return (
    <div>
      <PageHeader
        title="Height Clearances"
        subtitle="Structure/crane/tower height clearances — auto-issued below threshold, else airspace officer assessment (M23 — SCAA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-40" placeholder="Ref, applicant, parcel…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isAirspaceOfficer ? (
              <button className="btn-primary" onClick={() => setShowSubmit(true)}>
                + Log clearance application
              </button>
            ) : null}
          </>
        }
      />

      {!isAirspaceOfficer ? <RoleNotice roles={AIRSPACE_OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No height clearances match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Structure</th>
                <th className="table-head">Proposed height</th>
                <th className="table-head">Applicant</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{c.refNo}</td>
                  <td className="table-cell text-xs text-slate-500">{c.structureDescription}</td>
                  <td className="table-cell tabular-nums text-xs">{num(c.proposedHeightM)} m</td>
                  <td className="table-cell font-mono text-xs">{c.applicantId}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showSubmit ? (
        <SubmitHeightClearanceModal
          onClose={() => setShowSubmit(false)}
          onCreated={() => {
            setShowSubmit(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <HeightClearanceDetailModal
          id={selectedId}
          canAct={isAirspaceOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function SubmitHeightClearanceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [applicantType, setApplicantType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [parcelNo, setParcelNo] = useState('');
  const [structureDescription, setStructureDescription] = useState('');
  const [proposedHeightM, setProposedHeightM] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/height-clearances', {
        applicantType,
        applicantId: applicantId.trim(),
        parcelNo: parcelNo.trim(),
        structureDescription: structureDescription.trim(),
        proposedHeightM: Number.parseFloat(proposedHeightM),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  const valid = applicantId.trim() && parcelNo.trim() && structureDescription.trim().length >= 3 && proposedHeightM;

  return (
    <Modal title="Log a height clearance application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Applicant type">
          <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="PERSON">Person</option>
            <option value="ENTITY">Entity</option>
          </select>
        </Field>
        <Field label="Applicant id" className="col-span-2">
          <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
        </Field>
        <Field label="Cadastral parcel no.">
          <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
        </Field>
        <Field label="Proposed height (m AGL)">
          <input className="field-input" type="number" value={proposedHeightM} onChange={(e) => setProposedHeightM(e.target.value)} />
        </Field>
        <Field label="Structure description" className="col-span-2 sm:col-span-3">
          <textarea className="field-input" rows={2} value={structureDescription} onChange={(e) => setStructureDescription(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Submitting…' : 'Submit — runs the obstacle-surface auto-check'}
      </button>
    </Modal>
  );
}

function HeightClearanceDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<HeightClearance>(`/api/v1/m23/height-clearances/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [note, setNote] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [reason, setReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const c = q.data;

  return (
    <Modal title={c ? `Height clearance ${c.refNo}` : 'Height clearance'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="State" value={<StatusBadge status={c.state} />} />
            <Detail label="Proposed height" value={`${num(c.proposedHeightM)} m AGL`} />
            <Detail label="Obstacle intersect" value={c.obstacleIntersects === undefined || c.obstacleIntersects === null ? '—' : c.obstacleIntersects ? 'Yes' : 'No'} />
            <Detail label="Parcel" value={c.parcelNo ?? '—'} />
            {c.district ? <Detail label="District" value={c.district} /> : null}
            {c.validTo ? <Detail label="Valid to" value={formatDate(c.validTo)} /> : null}
            {c.referralSourceModule ? <Detail label="Referred from" value={`${c.referralSourceModule} · ${c.referralCaseRef ?? ''}`} /> : null}
          </div>
          <Detail label="Structure" value={c.structureDescription} />
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}

          {c.state === 'REQUIRES_REVIEW' ? (
            canAct ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Officer assessment</div>
                <div className="flex gap-2">
                  <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                    Approve
                  </button>
                  <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                    Reject
                  </button>
                </div>
                {decision ? (
                  <>
                    <Field label="Note">
                      <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                    </Field>
                    <button
                      className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || note.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m23/height-clearances/${c.id}/decide`, { decision, note: note.trim() }))}
                    >
                      {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()} (creates assessment fee intent)`}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={AIRSPACE_OFFICER_ROLES} />
            )
          ) : null}

          {canAct && !['ISSUED', 'AUTO_APPROVED', 'REJECTED', 'WITHDRAWN'].includes(c.state) ? (
            !showWithdraw ? (
              <button className="btn-secondary" onClick={() => setShowWithdraw(true)}>
                Withdraw
              </button>
            ) : (
              <div className="flex items-end gap-2">
                <Field label="Reason (optional)">
                  <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/height-clearances/${c.id}/withdraw`, reason.trim() ? { reason: reason.trim() } : {}))}>
                  Confirm withdraw
                </button>
              </div>
            )
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
